/**
 * 올린 HTML 안의 영상(2026-10-30) — [화면 편집] 의 [넣기] ▸ [영상].
 *
 * 영상은 그림처럼 문서에 품기엔 크다(문서 상한 10MB, 5.1 셋톱은 문서를 통째로 글자로 읽는다). 그래서 영상은 콘텐츠로 따로
 * 올리고(서버가 화면이 푸는 MP4 로 바꾼다) 문서에는 그 자산 id 만 적는다 — `<video data-so-video="<자산 id>">`.
 *
 * **영상은 칸 밖에서 튼다.** 화면은 올린 HTML 을 출처 없는 칸(샌드박스)에 띄우는데, 5.1 웹뷰(Chromium 39)는 그런 칸 안에서
 * Blob 주소의 영상을 틀지 못한다 — 칸이 만든 주소든 부모가 만든 주소든, 디스크에 내려진 Blob 이든 오류 4(지원 안 되는 원본)다.
 * 같은 Blob 을 부모 문서에서는 튼다(롤리팝 에뮬레이터 실측 2026-10-30). 그래서:
 *
 * 1. 서버가 HTML 을 받을 때 가리키는 영상을 적어 두고(`html_media`), 재생 항목에 `media` 로 싣는다 — 화면이 HTML 과 함께
 *    받아 두어 인터넷이 끊겨도 돈다.
 * 2. 문서 끝의 작은 스크립트(콘솔 `htmlEditor/components.ts` 의 `VIDEO_SCRIPT`)가 영상의 자리(칸 안의 좌표·크기)와 맞춤·소리를
 *    부모에게 알린다(`HTML_MEDIA_REQUEST`). 다 읽힌 뒤·칸 크기가 바뀔 때 다시 알린다.
 * 3. 화면(콘솔 미리보기도)이 칸 위에 겹친 층에 그 자리대로 제 영상을 놓고 받아 둔 파일로 튼다 — 플레이어가 영상을 트는
 *    늘 쓰던 길이다. 층은 칸과 같은 크기·배율이라 자리가 맞는다.
 *
 * 그래서 문서 안에서 영상 **위에** 겹친 글·그림은 화면에서 영상에 가려진다. 옛 화면(이 약속을 모르는 에이전트)에서는 영상
 * 자리가 검은 채로 나머지가 그대로 나간다.
 */
/** 문서의 영상이 가리키는 자산 — `<video data-so-video="<자산 id>">`. */
export const VIDEO_ATTR = 'data-so-video';
/** 칸 → 부모: "이 자리에 이 영상을" — `{ type, videos: HtmlMediaBox[] }`. 같은 칸이 다시 보내면 그것으로 바꾼다. */
export const HTML_MEDIA_REQUEST = 'signageone:media-request';
const finite = (value, min, max) => typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : null;
/**
 * 칸이 보낸 알림인가 — 남이 짠 문서가 보내는 것이라 모양을 믿지 않는다. 자산 id 꼴이고 자리가 숫자인 것만, 20개까지.
 * 알림이 아니면 null.
 */
export function readMediaRequest(data) {
    if (!data || typeof data !== 'object' || data.type !== HTML_MEDIA_REQUEST)
        return null;
    const videos = data.videos;
    if (!Array.isArray(videos))
        return null;
    const boxes = [];
    for (const video of videos.slice(0, 20)) {
        if (!video || typeof video !== 'object')
            continue;
        const raw = video;
        const id = typeof raw.id === 'string' && /^[0-9a-f]{24}$/i.test(raw.id) ? raw.id.toLowerCase() : null;
        const x = finite(raw.x, -100_000, 100_000);
        const y = finite(raw.y, -100_000, 100_000);
        const width = finite(raw.width, 0, 100_000);
        const height = finite(raw.height, 0, 100_000);
        if (!id || x === null || y === null || !width || !height)
            continue;
        boxes.push({ id, x, y, width, height, fit: raw.fit === 'contain' ? 'contain' : 'cover', muted: raw.muted !== false });
    }
    return boxes;
}
const VIDEO_REFERENCE = /\bdata-so-video\s*=\s*["']?([0-9a-f]{24})\b/gi;
/** 문서가 가리키는 영상 자산 id — 나온 차례대로, 겹치지 않게. */
export function htmlMediaIds(html) {
    const ids = [];
    for (const match of html.matchAll(VIDEO_REFERENCE)) {
        const id = match[1].toLowerCase();
        if (!ids.includes(id))
            ids.push(id);
    }
    return ids;
}
//# sourceMappingURL=html-media.js.map